import { useMutation } from '@tanstack/react-query';
import { rollReviewRepository } from '../repositories/supabaseRollReviewRepository';
import { monitoring } from '@/lib/monitoring';

/**
 * Best-effort discard of an uploaded-but-unpaid roll review video.
 *
 * Wraps `rollReviewRepository.discardPendingVideo` in a mutation so callers
 * get isPending state for the confirm-dialog button. Failures are reported
 * to Sentry under `stage: 'discard_pending_video'` but never rejected: the
 * UI must always be able to navigate forward (orphans are acceptable —
 * see docs/video-upload-recovery.md gap G2).
 */
export interface DiscardPendingVideoInput {
  videoId:     string;
  storagePath: string;
}

export const useDiscardPendingVideo = () => {
  return useMutation({
    mutationFn: async ({ videoId, storagePath }: DiscardPendingVideoInput) => {
      const result = await rollReviewRepository.discardPendingVideo(videoId, storagePath);

      if (!result.ok) {
        monitoring.captureException(
          new Error('[useDiscardPendingVideo] partial cleanup failure'),
          {
            stage:        'discard_pending_video',
            videoId,
            storagePath,
            storageError: result.storageError ?? null,
            rowError:     result.rowError ?? null,
          },
        );
      }

      return result;
    },
  });
};
